import React, { type FunctionComponent } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import {
  bannerDestinationTypes,
  placementDestinationOptions,
} from 'domains/banners/constants/banner.constants'
import { useSelectOptionsDashMessage } from 'domains/banners/utils/intl.hooks'
import { MerchandisingFormInput } from 'domains/merchandising/components/form-input/MerchandisingFormInput'
import { type BannerFormData } from 'domains/banners/types/banner.types'

interface Props {
  form: FormContextValues<BannerFormData>
  defaultDisplayValue: string
}

export const BannerFormLinkDestination: FunctionComponent<Props> = ({ form }) => {
  const { watch, register, setValue } = form
  const destinationType: string = watch('destination.type') || bannerDestinationTypes.doNotNavigate
  const destinationTarget: string = watch('destination.target') || ''
  const openInNewTab: string = watch('destination.external.open_in_new_tab') || 'false'
  const showDistance: string = watch('destination.pickupstoreselector.show_distance') || 'false'
  const isDoNotNavigate = destinationType === bannerDestinationTypes.doNotNavigate
  const isExternal = destinationType === bannerDestinationTypes.external
  const isStorefront = destinationType === bannerDestinationTypes.storefront
  const isStoreSelector = destinationType === bannerDestinationTypes.storeSelector
  const isPickupStoreSelector = destinationType === bannerDestinationTypes.pickupStoreSelector
  const destinationOptions = useSelectOptionsDashMessage(placementDestinationOptions)

  return (
    <MerchandisingFormSection
      titleId="bannersDomain.bannersForm.form.link-destination.title"
      tooltipId="bannersDomain.bannersForm.form.link-destination.tooltip"
      renderAction
    >
      <input type="hidden" value={destinationTarget} name="destination.target" ref={register} />
      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingFormSelect
            form={form}
            labelId="bannersDomain.bannersForm.form.link-destination.input.destinationType.label"
            identifier="destination.type"
            onChange={() => setValue('destination.target', null)}
            options={destinationOptions}
            defaultValue={destinationType}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>
      {isDoNotNavigate &&
        setValue(
          'destination.donotnavigate.record_click_attempt',
          '{"record_click_attempt":false}'
        )}
      {isExternal && (
        <div>
          <MerchandisingFormRow>
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.external.url"
                identifier="destination.external.url"
                defaultValue={''}
              />
            </MerchandisingFormField>
          </MerchandisingFormRow>
          <MerchandisingFormRow>
            <MerchandisingFormField fullWidth>
              <MerchandisingFormSelect
                form={form}
                labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.external.open-in-new-tab"
                identifier="destination.external.open_in_new_tab"
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                onChange={({ value }) => setValue('destination.external.open_in_new_tab', value)}
                defaultValue={openInNewTab}
              />
            </MerchandisingFormField>
          </MerchandisingFormRow>
        </div>
      )}
      {/* TODO: Validate Retailer Id entered */}
      {isStorefront && (
        <MerchandisingFormRow>
          <MerchandisingFormField>
            <MerchandisingFormInput
              ref={register({ required: true })}
              labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.storefront.retailer-id"
              identifier="destination.storefront.retailerId"
              defaultValue={''}
            />
          </MerchandisingFormField>
        </MerchandisingFormRow>
      )}

      {/* TODO: Validate list of retailer ids entered */}
      {isStoreSelector && (
        <MerchandisingFormRow>
          <MerchandisingFormField>
            <MerchandisingFormInput
              ref={register({ required: true })}
              labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.storeselector"
              identifier="destination.storeselector.retailerIds"
              defaultValue={[]}
            />
          </MerchandisingFormField>
        </MerchandisingFormRow>
      )}

      {isPickupStoreSelector && (
        <MerchandisingFormRow>
          <MerchandisingFormField>
            <MerchandisingFormSelect
              form={form}
              labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.pickup-storeselector.show-pickup-distance"
              identifier="destination.pickupstoreselector.show_distance"
              options={[
                { value: 'true', label: 'True' },
                { value: 'false', label: 'False' },
              ]}
              defaultValue={showDistance}
              onChange={({ value }) =>
                setValue('destination.pickupstoreselector.show_distance', value)
              }
            />
          </MerchandisingFormField>
        </MerchandisingFormRow>
      )}
    </MerchandisingFormSection>
  )
}
